import { useQuery, useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight } from 'lucide-react';
import { OrgAlert } from '@/components/common/OrgAlert';
import { useOrgs } from '@/contexts/OrgsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { postListProfiles, getProfilesById } from '@/api/control/control-plane/control-plane';
import { useGetOrganizationProductEntitlements } from '@/api/auth/product-entitlements/product-entitlements';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { PastDueAlertContent } from '@/components/billing/PastDueAlertContent';
import { TrialExpirationAlertContent } from '@/components/billing/TrialExpirationAlertContent';
import { NoEntitlementsAlertContent } from '@/components/billing/NoEntitlementsAlertContent';
import type {
  ProfileDto,
} from '@/api/control/missionImpactAPI.schemas';

/**
 * OrgAlerts component displays organization alerts and notifications.
 * Shows alerts for incomplete profiles and past due subscriptions.
 */
export function OrgAlerts() {
  const { selectedOrganizationId } = useOrgs();
  const { t } = useLanguage();

  // Fetch entitlements to check for past due subscriptions
  const { data: entitlementsResponse } = useGetOrganizationProductEntitlements(
    selectedOrganizationId ?? '',
    'data-mission',
    undefined,
    {
      query: {
        enabled: !!selectedOrganizationId,
      },
    }
  );

  const entitlements = entitlementsResponse?.data.entitlements ?? [];
  const hasNoEntitlements = entitlementsResponse?.data && entitlements.length === 0;
  const hasPastDueSubscription = entitlements.some(
    (e) => e.isSubscription && e.subscriptionStatus === 'past_due'
  );
  const hasActiveSubscription = entitlements.some((e) => e.isSubscription);

  // Check for trial expiring within 7 days (only if no active subscription)
  const expiringTrial = !hasActiveSubscription
    ? entitlements.find((e) => {
        if (!e.isTrial && !e.isDefault) return false;
        if (!e.expirationDate) return false;
        const expirationDate = new Date(e.expirationDate);
        const now = new Date();
        const daysUntilExpiration = Math.ceil(
          (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysUntilExpiration > 0 && daysUntilExpiration <= 7;
      })
    : null;

  const trialDaysRemaining = expiringTrial?.expirationDate
    ? Math.ceil(
        (new Date(expiringTrial.expirationDate).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  // Fetch profiles list
  const { data: profilesData, isLoading: isLoadingProfiles, error: profilesError, refetch: refetchProfiles } = useQuery({
    queryKey: ['profiles', 'list', selectedOrganizationId],
    queryFn: async () => {
      const response = await postListProfiles(selectedOrganizationId ?? '', { pageSize: 100 });
      return response;
    },
    enabled: !!selectedOrganizationId,
    retry: 1,
  });

  const profiles: ProfileDto[] = profilesData?.data.items ?? [];

  // Fetch detailed profile data for each profile to check completeness
  const profileQueries = useQueries({
    queries: profiles.map((profile) => ({
      queryKey: ['profiles', 'detail', selectedOrganizationId, profile.id],
      queryFn: async () => {
        const response = await getProfilesById(selectedOrganizationId ?? '', profile.id);
        return response.data;
      },
      enabled: !!selectedOrganizationId && !!profile.id,
      retry: 1,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    })),
  });

  // Check if still loading any profile details
  const isLoadingDetails = profileQueries.some((query) => query.isLoading);
  
  // Check if any profile detail queries have errors
  const hasProfileDetailErrors = profileQueries.some((query) => query.error);

  // Show error if profiles list failed to load
  if (profilesError) {
    return (
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <CardTitle>{t.dashboard.alerts.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryErrorState 
            error={profilesError}
            message={t.errors.profiles.failedToLoadProfiles}
            onRetry={() => void refetchProfiles()}
          />
        </CardContent>
      </Card>
    );
  }

  // Show error if profile details failed to load
  if (hasProfileDetailErrors && !isLoadingDetails) {
    return (
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <CardTitle>{t.dashboard.alerts.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryErrorState 
            error={profileQueries.find(q => q.error)?.error}
            message={t.errors.profiles.failedToLoadProfileDetails}
            onRetry={() => { profileQueries.forEach(q => void q.refetch()); }}
          />
        </CardContent>
      </Card>
    );
  }

  // Find incomplete profiles (missing source or target)
  const incompleteProfiles = profiles
    .map((profile, index) => {
      const detailedProfile = profileQueries[index]?.data;
      return {
        profile,
        detailedProfile,
        missingSource: !detailedProfile?.source,
        missingTarget: !detailedProfile?.target,
      };
    })
    .filter((item) => item.missingSource || item.missingTarget);

  // Show loading state
  if (isLoadingProfiles || isLoadingDetails) {
    return (
      <Card className="flex-shrink-0 w-fit min-w-[350px]">
        <CardHeader className="pb-3">
          <CardTitle>{t.dashboard.alerts.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build list of alerts
  const alerts: React.ReactNode[] = [];

  // No entitlements alert
  if (hasNoEntitlements) {
    alerts.push(<NoEntitlementsAlertContent key="no-entitlements" />);
  }

  // Past due subscription alert
  if (hasPastDueSubscription) {
    alerts.push(<PastDueAlertContent key="past-due" />);
  }

  // Trial expiration alert
  if (expiringTrial && trialDaysRemaining > 0) {
    alerts.push(<TrialExpirationAlertContent key="trial-expiring" daysRemaining={trialDaysRemaining} />);
  }

  // Incomplete profile alerts
  incompleteProfiles.forEach((item) => {
    const nextStep = item.missingSource ? 'source' : 'target';
    const setupUrl = `/dashboard/orgs/${selectedOrganizationId ?? ''}/profiles/${item.profile.id}/setup/${nextStep}`;

    alerts.push(
      <OrgAlert
        key={item.profile.id}
        variant="warning"
        title={t.dashboard.alerts.incompleteProfile}
      >
        <p className="text-xs">
          {t.dashboard.alerts.profileMissing} <strong>{item.profile.name}</strong> {t.dashboard.alerts.isMissing}
        </p>
        <ul className="text-xs list-disc list-inside">
          {item.missingSource && <li>{t.dashboard.alerts.sourceConfiguration}</li>}
          {item.missingTarget && <li>{t.dashboard.alerts.targetConfiguration}</li>}
        </ul>
        <div className="mt-auto self-end">
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-warning text-warning hover:opacity-90"
          >
            <Link to={setupUrl}>
              {t.dashboard.alerts.completeSetup}
              <ArrowRight className="ml-2 h-3 w-3" />
            </Link>
          </Button>
        </div>
      </OrgAlert>
    );
  });

  // No alerts
  if (alerts.length === 0) {
    return null;
  }

  return (
    <Card className="flex-shrink-0 w-fit">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          {t.dashboard.alerts.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-3">
        {alerts}
      </CardContent>
    </Card>
  );
}
